// includes/js-src/janus-engine/pl/xref.ts — refusals + the cross-reference read the PL pricer makes (Janus C2).
// PHP reads another item's STORED bare PL through ft_pl_xref_cached_read() → pl_total(); a cold miss there is a floor. The
// browser never floors: a target it cannot see is a REFUSAL (plan D7, §14 amendments 5/12).
import type { ItemFieldRow } from '../compile/item-field-reader';
import { isPhpNumeric, phpJsonFloat } from '../compile/php-decimal-values';
import type { PlReadContext, Row } from './read-context';
import { isObj } from './read-context';
import { plCostContract } from './contract';

import { PlRefusal } from './refusal';
export { PlRefusal };
/** pl_total(): the bare PL number of a compiled arr (de-scalared, or the legacy {0: n} shape); null when absent. */
export function plTotal(arr: unknown): number | null {
    if (!isObj(arr)) return null;
    const computed = arr.computed;
    let pl: unknown = isObj(computed) && computed.pl !== undefined ? computed.pl : arr.pl;
    if (pl === undefined || pl === null) return null;
    if (isObj(pl) || Array.isArray(pl)) {
        const zero = (pl as Row)['0'];
        return zero !== undefined && zero !== null && isPhpNumeric(zero) ? phpJsonFloat(zero) : null;
    }
    return isPhpNumeric(pl) ? phpJsonFloat(pl) : null;
}

/** A dependency value must belong to this browser pricing contract. Display overlays are page-local browser results. */
export function plDependencyTotal(arr: unknown, contractKey = Object.values(plCostContract.sources).join('.')): number | null {
    if (!isObj(arr) || !isObj(arr.computed) || arr.computed.pl_pending) return null;
    const meta = arr.computed.pl_meta;
    if (!isObj(meta) || meta.contract !== contractKey) return null;
    if (meta.source !== 'browser-frozen' && meta.source !== 'browser-computed-unsaved') return null;
    if (meta.source === 'browser-frozen' && isObj(arr._pl_stash) && arr._pl_stash.verdict !== 'hit') return null;
    return plTotal(arr);
}

/** The creatures a read context is pricing right now (calc-pl.ts pushes a creature / PC root's item id) — a summon of one of them is a
 *  cycle the pricer skips (ability-cost.ts readSummonTarget). Per context, so concurrent builds never share it. */
const pricingStacks = new WeakMap<object, string[]>();
export function plPricingStack(ctx: object): string[] {
    let stack = pricingStacks.get(ctx);
    if (!stack) pricingStacks.set(ctx, stack = []);
    return stack;
}

export interface XrefTarget { pl: number; name: string; def: Row }
/** ft_pl_xref_cached_read($id) + pl_total(): the target's definition and its stored PL; refuses when the browser cannot see it. */
export function readXrefTarget(ctx: PlReadContext, id: string): XrefTarget {
    let def = ctx.definition(id);
    if (!def) throw new PlRefusal((ctx.refused.get(id.toUpperCase()) === 'forbidden' ? 'pl-def-forbidden:' : 'pl-def-missing:') + id.toUpperCase());
    let pl = plDependencyTotal(def);
    if (pl === null) {
        // The encounter root catalog interns definitions with their sparse computed defaults elided (object API v39), so a
        // catalog copy can lack the very number PHP reads from the Redis blob. The STORE copy (object_inc payload) carries it.
        const stored = ctx.storedDefinition(id);
        if (stored) { def = stored; pl = plDependencyTotal(stored); }
    }
    if (pl === null) throw new PlRefusal('pl-def-nopl:' + id.toUpperCase());
    const name = ctx.itemField(def as ItemFieldRow, 'item_name');
    return { pl, name: typeof name === 'string' ? name : '?', def };
}
/** query_full_item_arr($id) for a definition the pricer PRICES (an ability / spell entry): FULL def or a refusal. */
export function requireDefinition(ctx: PlReadContext, id: string): Row {
    const def = ctx.definition(id);
    if (!def) throw new PlRefusal((ctx.refused.get(id.toUpperCase()) === 'forbidden' ? 'pl-def-forbidden:' : 'pl-def-missing:') + id.toUpperCase());
    return def;
}
